/**
 * Where third-party analytics may and may not run.
 *
 * Single source of truth, imported by both the document shell (which decides
 * whether to emit tracker tags at all) and the cookie-consent component (which
 * injects session replay after consent). Two lists would drift, and a drifted
 * list here means student data reaching a vendor.
 *
 * Two reasons a route appears below:
 *
 * 1. Secrets in the URL. Auth links carry tokens (?token= on reset-password).
 *    Trackers record the full URL and would leak them to analytics logs and via
 *    the Referer header.
 *
 * 2. Student and family data on screen. We hold education records on behalf of
 *    school districts, so that data must not be redisclosed to vendors the
 *    district never agreed to. Page-view trackers see only the URL, but
 *    session replay records the rendered DOM: on the dashboard that is every
 *    parent's name, email and phone plus every student name, and on the
 *    parent-facing pages it is the child's name as the parent types it.
 *
 * This is a denylist of route prefixes, so a new page under one of these trees
 * is private by default rather than needing to remember to opt out.
 */
export const NO_ANALYTICS_PATHS = [
  // Secrets in the URL
  "/reset-password",
  "/forgot-password",
  "/verify-email",
  // Student / family data on screen
  "/dashboard",
  "/go",
  "/my-schedule",
  "/manage",
  "/portal",
  "/plan",
  "/admin",
  "/checkin",
];

/** Public school landing pages are /:schoolId, a bare 6-digit id. */
const SCHOOL_ID_ROUTE = /^\/\d{6}(\/|$)/;

/** False when no third-party script may load or run on this path. */
export function analyticsAllowedFor(pathname: string): boolean {
  if (!pathname) return false;
  if (SCHOOL_ID_ROUTE.test(pathname)) return false;
  return !NO_ANALYTICS_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

/**
 * Remove any session-replay tag already in the document. Consent is stored in
 * localStorage, so an admin who accepted on the marketing site arrives at the
 * dashboard with the tag already injected; client-side navigation alone would
 * never take it back out.
 */
export function removeSessionReplay() {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll('script[src*="contentsquare.net"]')
    .forEach((el) => el.remove());
}
